import React, { useState } from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";

import {
  HiOutlineKey,
  HiArrowLongRight,
  HiOutlineRocketLaunch,
} from "react-icons/hi2";
import { MdLibraryBooks } from "react-icons/md";
import { TbApi } from "react-icons/tb";
import { PiWebhooksLogo, PiChatsLight } from "react-icons/pi";
import { PiFileHtml } from "react-icons/pi";
import { IoDocumentTextOutline } from "react-icons/io5";
import { BsPersonPlus } from "react-icons/bs";
import { IoSearchOutline } from "react-icons/io5";
import { TfiDownload } from "react-icons/tfi";
import { FaUserShield, FaLaptopCode } from "react-icons/fa6";
import { IoCopyOutline } from "react-icons/io5";
import { MdStars } from "react-icons/md";
import { CgTerminal } from "react-icons/cg";
import { FaBookOpen } from "react-icons/fa6";
import { AiOutlineNodeIndex } from "react-icons/ai";
import { SiPostman } from "react-icons/si";
import ApiAddonPricingCard from "@components/Pricing/ApiAddonPricingCard";

import { BASE_ASSETS_URL } from "@constants/constants";

import Slider from "@components/Slider";
import CtaOne from "@components/CTA/CtaOne";
import FaqCard from "@components/Cards/FaqCard";
import {
  ButtonPrimary,
  ButtonSecondary,
  RoundButton,
} from "@components/Buttons";
import PricingCards from "@components/Cards/PricingCards";
import Subscribe from "@components/Subscribe";
import ClientsTestimonial from "@components/Cards/ClientsTestimonial";
import { createUTMQueryParams } from "@utils/signature_owner";
import CustomersComponent from "components/customers";
import {
  CLMButtonPrimary,
  CLMButtonPrimaryArrow,
  CLMButtonSecondaryBorder,
} from "components/CLM/v3/Buttons";
import FAQ2 from "components/CLM/FAQ2";
import CustomersMarquee from "components/CLM/Customers";
import { industryLeadersCard } from "@constants/customersData";
import ApiAddonPricingCard2 from "@components/Pricing/ApiAddonPricingCard2";

const customerImages = [
  "company1.png",
  "company2.png",
  "company3.png",
  "company4.png",
  "company5.png",
  "company6.png",
  "company7.png",
];

const developerFriendlyApi = [
  {
    icon: <CgTerminal />,
    title: "Easily integrate BoloSign into your existing workflows",
    description:
      "Build solutions with zero assistance with our extensive documentation, how-to guides, and real examples. Test your API calls with 7 days free trial.",
    bgColor: "bg-accent-orange",
  },
  {
    icon: <FaUserShield />,
    title: "Keep your data protected with top-tier security",
    description:
      "Signature, along with the corresponding documentation, are stored in a secure infrastructure. Our e-Signature software is E-SIGN, UETA and HIPAA compliant. It's also backed by SOC 2 certification so you can sign with total confidence.",
    bgColor: "bg-accent-blue",
  },
  {
    icon: <PiChatsLight />,
    title: "Stay in the loop with Developer Dashboard",
    description:
      "Elevate your development experience with our Developer Center. Monitor production data in real-time, maintain a comprehensive history of webhooks and keep track of request and response times for optimal performance.",
    bgColor: "bg-accent-pink",
  },
];

const cardsData = [
  {
    title: "REST API",
    description:
      "Utilize the power of our REST API for seamless integration and outstanding performance.",
    icon: <TbApi />,
    bgColor: "bg-orange-100",
    iconColor: "bg-orange-500",
  },
  {
    title: "Webhooks",
    description:
      "Enhance your workflow with our reliable webhooks for real-time data updates and automation.",
    icon: <PiWebhooksLogo />,
    bgColor: "bg-fuchsia-100",
    iconColor: "bg-fuchsia-500",
  },
  {
    title: "HTML to PDF",
    description:
      "Create fillable document forms using our HTML API with precise and rich styling capabilities.",
    icon: <PiFileHtml />,
    bgColor: "bg-green-100",
    iconColor: "bg-green-500",
  },
];

const restAPiCards = [
  {
    title: "Templates",
    description:
      "Send documents with pre-filled fields and create templates for future use.",
    icon: <IoDocumentTextOutline />,
    bgColor: "bg-sky-500",
  },
  {
    title: "Submissions",
    description:
      "Create new submissions to request signatures and load data about completed submissions.",
    icon: <BsPersonPlus />,
    bgColor: "bg-green-500",
  },
  {
    title: "Webhooks",
    description:
      "Analyse document signing events for each individual submission.",
    icon: <IoSearchOutline />,
    bgColor: "bg-orange-500",
  },
  {
    title: "Documents",
    description: "Download signed documents via URLs provided by the API.",
    icon: <TfiDownload />,
    bgColor: "bg-red-500",
  },
];

const faqs = [
  {
    id: 1,
    question: "Which plan do I need to be on to get API access?",
    answer: "Please see above pricing section.",
  },
  {
    id: 2,
    question: "How will I be charged for API usage?",
    answer:
      "Once you subscribe to a plan according to your needs, you will be charged according to the plan till the usage already defined in it. If number of requests exceds the limit you would be charged .5$/doc extra at the end of billing cycle.",
  },
  {
    id: 3,
    question: "What is the response time for API calls?",
    answer:
      "The response time for API calls is dependent on the server load and the complexity of the request. However, the average response time is less than 200ms.",
  },
  {
    id: 4,
    question: "Do I get embed with API?",
    answer:
      "BoloSign Embed is included in API plan. It is another way of sending documents from your system just like API. Pricings are same to the API plans",
  },
  {
    id: 5,
    question: "Do you provide a sandbox environment for testing?",
    answer:
      "Yes we provide sandbox environment in our 7 days free trial. You can test your API calls in the sandbox environment before going live by signing up in our dashboard. In case you need more time, you can request for it by contacting our support team from the chat or emailing us at support@boloforms.com",
  },
  // {
  //   question: "Fair Usage Policy",
  //   answer: `
  //     <div class="flex flex-col gap-2">
  //     <p>If you're reading this, you're not a small business anymore—congratulations on your success! While our unlimited plans are specifically designed for small businesses (typically between 1-25 employees), small businesses usually operate comfortably within the following limits:</p>
  //     <ul class="list-disc pl-8">
  //       <li>Up to 1200 PDFs per year</li>
  //       <li>Up to 1200 Forms per year</li>
  //       <li>Up to 12,000 Form Responses per year</li>
  //       <li>Up to 1200 PDF Templates per year</li>
  //     </ul>
  //     <p>We also offer tailored solutions for medium and large businesses, maintaining our commitment to being at least 10 times more affordable than providers like DocuSign or PandaDoc.</p>
  //     <p>If you want details on pricing tailored to your company's size or if you're unsure whether your usage fits within our fair use guidelines, please contact us directly at <a href="mailto:support@boloforms.com" class="text-violet-600 underline">support@boloforms.com</a>.</p>

  //     </div>
  //   `,
  // },
];

const resources = [
  {
    icon: <FaBookOpen className="text-4xl" />,
    title: "API Reference",
    description: "See all of the API methods, grouped by functionality.",
    link: "https://developer.boloforms.com/",
    bgColor: "bg-accent-green",
  },
  {
    icon: <AiOutlineNodeIndex className="text-4xl" />,
    title: "OpenAPI Specification",
    description: "Explore BoloSign API specification.",
    link: "https://developer.boloforms.com/",
    bgColor: "bg-accent-blue",
  },
  {
    icon: <SiPostman className="text-5xl" />,
    title: "Postman Collection",
    description: "Run sample requests, test sequences.",
    link: "https://developer.boloforms.com/",
    bgColor: "bg-accent-pink",
  },
  // {
  //   icon: <FaLaptopCode className="text-4xl" />,
  //   title: "SDK",
  //   description:
  //     "Call BoloSign APi from the code in yout favourite language. Integrate BoloSign into your web-based application with JavaScript SDK.",
  //   link: "https://developer.boloforms.com/",
  // },
];

const codeSnapData = [
  {
    text: (
      <>
        {`<signature-field>`}
        <br />
        {`</signature-field>`}
      </>
    ),
  },
  {
    text: (
      <>
        {`<text-field name="City">`}
        <br />
        {`</text-field>`}
      </>
    ),
  },
  {
    text: (
      <>
        {`<date-field>`}
        <br />
        {`</date-field>`}
      </>
    ),
  },
];

const apiFreePricing = {
  title: "Sandbox Testing",
  description: "Send 20 documents via API. Request more via chat.",
  price: 0,
  pricingType: "mo",
  priceDetail: "",
  featuresIcon: <MdStars />,
  recommended: false,
  features: ["20 Documents Included"],
  // pricingLink: "https://buy.stripe.com/28o7vPgAO3VXgG4fZ5",
};

const apiYearlyPricing = {
  title: "API",
  description:
    "Automate your signature workflow by integrating our API into your systems.",
  price: 20,
  pricingType: "mo",
  priceDetail: "+ $0.2/document",
  featuresIcon: <MdStars />,
  recommended: false,
  features: [
    "100 Documents/mo Included",
    "$0.2 per Document Sent",
    "Can be purchased with any plan",
  ],
  // pricingLink: "https://buy.stripe.com/28o7vPgAO3VXgG4fZ5",
};

const API = () => {
  return (
    <div className="w-full">
      <Head>
        <title>BoloSign - AI for Smart Contract Management | API</title>
        <meta
          name="description"
          content="Automate your signature workflow with Bolosign's powerful and easy-to-use API."
        />
        <meta
          name="keywords"
          content="esignature, e-signature, digital signature, api, boloforms, bolosign, api documentation, api reference, webhooks, html to pdf, rest api"
        />
        <link
          rel="icon"
          href="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/Logo.png"
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="BoloSign" />
        <meta name="publisher" content="BoloSign " />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />

        {/* <!-- Facebook Meta Tags --> */}
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/api/"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="BoloSign - AI for Smart Contract Management | API"
        />
        <meta
          property="og:description"
          content="Automate your signature workflow with Bolosign's powerful and easy-to-use API."
        />
        <meta
          property="og:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        {/* <!-- Twitter Meta Tags --> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta
          property="twitter:url"
          content="https://www.bolosign.com/signature/api/"
        />
        <meta
          name="twitter:title"
          content="BoloSign - AI for Smart Contract Management | API"
        />
        <meta
          name="twitter:description"
          content="Automate your signature workflow with Bolosign's powerful and easy-to-use API."
        />
        <meta
          name="twitter:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        <link rel="canonical" href="https://www.bolosign.com/signature/api/" />
      </Head>

      {/* section 1 */}
      <section className="bg-dark text-white w-full xl:h-[min(750px,calc(100vh-110px))] grid place-items-center ">
        <div className="max-w-screen-xl w-full h-full px-2 sm:px-4 flex flex-col lg:flex-row items-center justify-center gap-4">
          <div className="flex-1 py-6 sm:py-10 text-center lg:text-left">
            <div className="w-full max-w-screen-mxl mx-auto px-4 lg:px-8 xxl:px-0 flex flex-col gap-4 sm:gap-5">
              <h1 className="text-4xl sm:text-6xl font-bold leading-tight">
                BoloSign{" "}
                <span className="bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 bg-clip-text text-transparent">
                  API
                </span>
              </h1>
              <p className="w-full text-lg sm:text-xl font-medium">
                Start for free and pay only for what you use.
              </p>
              <p className="w-full text-lg max-w-screen-md mx-auto">
                BoloSign provides a powerful and easy-to-use API to implement
                eSigning automation workflows for businesses. Try it now for
                free, no CC required.
              </p>
              <div className="flex items-center justify-center lg:justify-start flex-wrap gap-6 mt-4">
                <CLMButtonPrimary
                  buttonText="Get API Key"
                  icon={<HiOutlineKey />}
                  buttonUrl="https://signature.boloforms.com/settings?type=DEV_SEC&subType=APIKEY&from=API_PAGE"
                />

                <CLMButtonSecondaryBorder
                  buttonText="Documentation"
                  icon={<MdLibraryBooks />}
                  buttonUrl="https://developer.boloforms.com/"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-1 py-6 justify-end">
            <Image
              src={require("@assets/api/hero-image.png")}
              alt="hero"
              width={400}
              height={400}
              className="w-full object-contain sm:scale-110 lg:scale-100 xl:scale-110 h-full aspect-square max-w-md lg:max-w-lg max-h-[min(600px,calc(100vh-200px))] xl:min-h-[450px] rounded-xl overflow-hidden"
            />
          </div>
        </div>
      </section>

      <section className="px-4 lg:px-8 py-20 md:py-28">
        <h1 className="max-w-5xl mx-auto text-3xl sm:text-5xl font-bold text-center mb-10 sm:mb-16">
          Pricing and Features
        </h1>
        <div className="max-w-screen-xl mx-auto md:px-8 px-4">
          <ApiAddonPricingCard2 currentPage={"api"} />
        </div>
      </section>

      <section className="w-full py-20 bg-dark">
        <div className="w-[1280px] max-w-full mx-auto ">
          <CustomersComponent />
        </div>
      </section>

      <section className="mt-32 max-w-screen-xl mx-auto px-2 sm:px-4">
        <h3 className="max-w-5xl mx-auto text-3xl sm:text-5xl font-bold text-center">
          Leverage BoloSign&apos;s developer-friendly API to create the perfect
          solution
        </h3>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
          {developerFriendlyApi.map((card, index) => (
            <div
              key={index}
              className={` px-6 py-8 rounded-xl border border-surface-dark bg-surface-light  transition-all duration-500 ease-in-out`}
            >
              <div className="w-16 h-16 flex rounded-full text-6xl">
                {card.icon}
              </div>
              <h3 className="text-2xl font-semibold mt-4 mb-3">{card.title}</h3>
              <p className="text-base">{card.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* cards  */}
      {/* <section className="mt-32 max-w-screen-xl mx-auto px-4 lg:px-8 xxl:px-0">
        <h3 className="max-w-5xl mx-auto text-3xl sm:text-5xl font-bold text-center">
          Build document workflows into your product
        </h3>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
          {cardsData.map((card, index) => (
            <div
              key={index}
              className={`w-full flex flex-col px-6 py-8 rounded-xl ${card.bgColor} text-center border border-gray-800 cardBorders transition-all duration-500 ease-in-out`}
            >
              <div
                className={`w-16 h-16 flex items-center justify-center rounded-full mx-auto text-white text-4xl ${card.iconColor}`}
              >
                {card.icon}
              </div>
              <h3 className="text-2xl font-semibold mt-4 mb-3">{card.title}</h3>
              <p className="text-base">{card.description}</p>
            </div>
          ))}
        </div>
      </section> */}

      {/* sections */}
      <div className="flex flex-col gap-16 md:gap-4 w-full max-w-screen-xl mx-auto mt-20 sm:mt-40 px-4 lg:px-8 xxl:px-0">
        {/* rest api  */}
        <div className="w-full flex flex-col lg:flex-row justify-between items-center gap-8">
          <div className="flex-1">
            <CtaOne
              heading2="API and Webhooks"
              paragraph="Discover the capabilities of our API, designed to effortlessly integrate data, automate tasks, and simplify document signing and processing."
              buttonPrimaryText="API Reference"
              buttonPrimaryUrl="https://developer.boloforms.com/"
              buttonSecondaryText="Webhooks Reference"
              buttonSecondaryUrl="https://developer.boloforms.com/webhooks/how-to-add-webhooks"
            />
          </div>
          <div className="max-w-2xl mx-auto flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="max-w-sm sm:max-w-none mx-auto flex flex-col gap-6">
              {restAPiCards.slice(0, 2).map((card, index) => (
                <div
                  key={index}
                  className={`w-full flex flex-col px-6 py-8 rounded-xl border-surface-dark bg-surface-light    text-black border-2  transition-all duration-500 ease-in-out`}
                >
                  <div
                    className={`w-16 h-16 sm:w-20 sm:h-20 bg-white flex items-center justify-center text-black text-3xl border border-surface-dark rounded-lg `}
                  >
                    {card.icon}
                  </div>
                  <h3 className="text-xl font-semibold mt-4 mb-3">
                    {card.title}
                  </h3>
                  <p className="text-base">{card.description}</p>
                </div>
              ))}
            </div>
            <div className="max-w-sm sm:max-w-none mx-auto flex flex-col gap-6 justify-end">
              {restAPiCards.slice(2, 4).map((card, index) => (
                <div
                  key={index}
                  className={`w-full flex flex-col px-6 py-8 rounded-xl border-surface-dark bg-surface-light    text-black border-2  transition-all duration-500 ease-in-out`}
                >
                  <div
                    className={`w-16 h-16 sm:w-20 sm:h-20 bg-white flex items-center justify-center text-black text-3xl border border-surface-dark rounded-lg `}
                  >
                    {card.icon}
                  </div>
                  <h3 className="text-xl font-semibold mt-4 mb-3">
                    {card.title}
                  </h3>
                  <p className="text-base">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* webhooks  */}
        {/* <div className="w-full flex flex-col md:flex-row-reverse justify-between items-center md:gap-10 xl:gap-20">
            <div className="w-full flex-1">
              <CtaOne
                heading2="Webhooks"
                paragraph="Collect real-time events and enhance your document signing workflows by building automations with our robust and reliable webhooks."
                buttonPrimaryText="Explore Now"
                buttonPrimaryUrl="https://help.boloforms.com/en/articles/8850090-how-to-add-webhooks"
              />
            </div>
            <div className="w-full flex-1">
              <Image
                src={`https://www.docuseal.co/packs/static/images/pages/server-4813aef7b371b88ee5b7.svg`}
                width={10}
                height={10}
                className="w-full h-auto"
                alt="server"
              />
            </div>
          </div> */}

        {/* html to pdf  */}
        {/*<div className="w-full flex flex-col md:flex-row justify-between items-center gap-16">
            <div className="w-full flex-1">
              <CtaOne
                heading2="HTML to PDF Form"
                paragraph="Try the convenience of our HTML to PDF form API, reducing the time for creating unique and personalized documents."
              />
              <div className="flex flex-col gap-2">
                {[
                  "Pixel-perfect positioning of fields.",
                  "Simple styling with CSS.",
                  "Complex layouts made easy with HTML.",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="text-2xl text-violet-500 grid place-items-center">
                      <FaRegCircleCheck />
                    </div>
                    <p className="text-gray-900 text-base">{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <ButtonPrimary buttonText="Read Guide" buttonUrl={``} />
              </div>
            </div>
            <div className="w-full sm:w-[45%] flex flex-col gap-8">
              {codeSnapData.map((code, index) => (
                <div
                  key={index}
                  className={`flex w-full ${
                    index === 1 ? "justify-end" : "justify-start"
                  }`}
                >
                  <MiniCodeSnap key={index} code={code.text} />
                </div>
              ))}
            </div>
          </div>*/}
      </div>

      {/* pricing  */}
      {/* <section id="pricing" className="bg-[#d7f3fe] mt-20 sm:mt-40 py-8">
        <div className="flex flex-col my-8 gap-8 md:gap-4 w-full max-w-screen-xl mx-auto px-4 lg:px-8 xxl:px-0">
          <h1 className="text-black text-4xl sm:text-6xl font-bold leading-tight text-center md:mb-10">
            API Pricing
          </h1>

          <div className="w-full flex flex-col items-stretch md:flex-row gap-6 sm:gap-10">
            <div className="w-full lg:flex-1">
              <PricingCards
                title={apiFreePricing.title}
                description={apiFreePricing.description}
                price={apiFreePricing.price}
                pricingType={apiFreePricing.pricingType}
                priceDetail={apiFreePricing.priceDetail}
                featuresIcon={apiFreePricing.featuresIcon}
                features={apiFreePricing.features}
                recommended={apiFreePricing.recommended}
                cardBorder={true}
                transparent={false}
                buttonText="Get Started"
                bgColor={"bg-pink-100"}
                buttonUrl={`https://signature.boloforms.com/register?from=API_PAGE${createUTMQueryParams(
                  {
                    from: "API_PAGE",
                  }
                )}`}
              />
            </div>

            <div className="w-full lg:flex-1">
              <PricingCards
                title={apiYearlyPricing.title}
                description={apiYearlyPricing.description}
                price={apiYearlyPricing.price}
                pricingType={apiYearlyPricing.pricingType}
                priceDetail={apiYearlyPricing.priceDetail}
                featuresIcon={apiYearlyPricing.featuresIcon}
                features={apiYearlyPricing.features}
                recommended={apiYearlyPricing.recommended}
                cardBorder={true}
                transparent={false}
                buttonText="Subscribe"
                bgColor={"bg-yellow-100"}
                isAnnualBilling={true}
              />
            </div>
          </div>
        </div>
      </section> */}
      <section className="bg-surface-dark py-20 mt-20">
        <div className="w-[1280px] max-w-full mx-auto pb-8">
          <h3 className="text-4xl md:text-5xl md:text-left text-center font-semibold  text-black">
            Industry leaders fly with BoloSign. <br /> It's your turn to rise
            with AI.
          </h3>
        </div>

        <CustomersMarquee customersData={industryLeadersCard} />
      </section>
      {/* <section className="pt-20 lg:pt-28 flex flex-col gap-6 relative max-w-screen-mxl mx-auto px-4 md:px-8 xxl:px-0">
        <ClientsTestimonial />
      </section> */}

      <section className="mt-20 max-w-screen-xl mx-auto px-4 md:px-8">
        <div className="md:w-2/3">
          <CtaOne
            heading2="Start leveraging the unique features of BoloSign API today"
            paragraph="Access functionalities that set us apart from the competition. We offer a range of unique features tailored to meet your needs."
          />
        </div>

        <div className="mt-4 xl:mt-8 grid md:grid-cols-3 gap-4 sm:gap-10">
          {resources.map((feature, index) => (
            <Link
              key={index}
              className={`block group w-[min(90vw,400px)] mx-auto md:w-auto md:mx-0 border  rounded-2xl transition-all duration-500 ease-in-out border-surface-dark bg-surface hover:bg-surface-light`}
              href={feature.link}
            >
              <div className="h-full flex flex-col space-y-6 p-8">
                <div>
                  <div className="bg-white text-black rounded-full w-20 h-20 flex items-center justify-center border border-surface-dark">
                    {feature.icon}
                  </div>
                </div>

                <div className="h-full flex flex-col justify-between space-y-6">
                  <div className="space-y-2">
                    <p className="text-xl font-semibold text-neutral-700 transition group-hover:text-secondary-focus">
                      {feature.title}
                    </p>
                    <p className="text-neutral-600">{feature.description}</p>
                  </div>
                  <div>
                    <CLMButtonPrimaryArrow buttonText={"Learn more"} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <section className="bg-surface-light text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 mt-24">
        <FAQ2 faqData={faqs} heading={"Frequently Asked Questions"} />
      </section>

      {/* <section className="mt-20">
        <Subscribe />
      </section> */}
    </div>
  );
};

export default API;

const MiniCodeSnap = ({ code }) => {
  return (
    <div className="rounded-xl bg-black py-3 px-4 w-[300px]">
      <div className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="w-2 h-2 rounded-full bg-gray-200"></div>
          ))}
        </div>
        <div className="flex items-center gap-2 text-white cursor-pointer">
          <IoCopyOutline /> COPY
        </div>
      </div>
      <div className="mt-3">
        <pre className="text-yellow-300 text-md">{code}</pre>
      </div>
    </div>
  );
};

const ApiPricingCard = ({
  title,
  titleColor,
  planType,
  price,
  description,
  buttonText,
  buttonUrl,
}) => {
  return (
    <div className="w-full lg:w-[450px] bg-pink-100 px-8 py-8 rounded-xl flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <h3 className={`text-2xl sm:text-3xl font-semibold ${titleColor}`}>
          {title}
        </h3>
        <div className="w-2 h-2 rounded-full bg-black"></div>
        <h3 className="text-3xl font-semibold text-black">{planType}</h3>
      </div>
      <div className="flex gap-3 items-end">
        <h3 className="text-5xl font-bold">{price}</h3>
        <p className="text-gray-900 text-lg">/ document</p>
      </div>
      <p className="text-gray-900 text-base">{description}</p>
      <div className="mt-2">
        <RoundButton buttonText={buttonText} buttonUrl={buttonUrl} />
      </div>
    </div>
  );
};
